/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, memo, useCallback } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { ToolCall } from "../../multimodal-live-types";
import { useMonitorAudio } from "../../hooks/useMonitorAudio";
import { FaVolumeMute, FaVolumeUp, FaTimes } from "react-icons/fa";
import "./vital-signs-monitor.scss";

// Name must match the function declaration in LiveAPIContext.tsx
const RENDER_ALTAIR_NAME = "render_altair";

// Supported ECG rhythm types
type ECGRhythm =
  | "normal_sinus"
  | "afib"
  | "aflutter"
  | "vfib"
  | "vtach"
  | "svt"
  | "asystole"
  | "bradycardia"
  | "tachycardia"
  | "hyperkalaemia";

interface VitalSigns {
  hr: number;
  bp_sys: number;
  bp_dia: number;
  spo2: number;
  rr: number;
  temp: number;
}

interface AltairProps {
  onCaseComplete?: (summary?: {
    ipsScore: number;
    dataGatheringScore: number;
    diagnosisMgtScore: number;
    areasForImprovement: string;
  }) => void;
}

// ─── ECG Rhythm Generators ────────────────────────────────────
function generateNormalSinus(hr: number, width: number, height: number): string {
  const midY = height / 2;
  const cycleWidth = Math.max(40, Math.min(120, 4800 / hr));
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width + cycleWidth) {
    path += ` L ${x + cycleWidth * 0.1} ${midY}`;
    path += ` Q ${x + cycleWidth * 0.15} ${midY - height * 0.08} ${x + cycleWidth * 0.2} ${midY}`;
    path += ` L ${x + cycleWidth * 0.28} ${midY}`;
    path += ` L ${x + cycleWidth * 0.3} ${midY + height * 0.06}`;
    path += ` L ${x + cycleWidth * 0.34} ${midY - height * 0.4}`;
    path += ` L ${x + cycleWidth * 0.38} ${midY + height * 0.15}`;
    path += ` L ${x + cycleWidth * 0.45} ${midY}`;
    path += ` Q ${x + cycleWidth * 0.55} ${midY - height * 0.12} ${x + cycleWidth * 0.65} ${midY}`;
    path += ` L ${x + cycleWidth} ${midY}`;
    x += cycleWidth;
  }
  return path;
}

function generateAFib(width: number, height: number): string {
  const midY = height / 2;
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width + 80) {
    // Irregular R-R intervals — key feature of AFib
    const interval = 30 + Math.random() * 50;
    // Fibrillatory baseline (no P waves, just tremor)
    for (let i = 0; i < 3; i++) {
      const jitter = (Math.random() - 0.5) * height * 0.06;
      path += ` L ${x + interval * (i / 3)} ${midY + jitter}`;
    }
    // Narrow QRS complex
    path += ` L ${x + interval * 0.4} ${midY + height * 0.04}`;
    path += ` L ${x + interval * 0.45} ${midY - height * 0.38}`;
    path += ` L ${x + interval * 0.5} ${midY + height * 0.12}`;
    path += ` L ${x + interval * 0.6} ${midY}`;
    // More fibrillatory baseline
    for (let i = 0; i < 3; i++) {
      const jitter = (Math.random() - 0.5) * height * 0.05;
      path += ` L ${x + interval * (0.6 + 0.4 * (i / 3))} ${midY + jitter}`;
    }
    path += ` L ${x + interval} ${midY}`;
    x += interval;
  }
  return path;
}

function generateAFlutter(width: number, height: number): string {
  const midY = height / 2;
  const cycleWidth = 60;
  let path = `M 0 ${midY}`;
  let x = 0;
  let beatCount = 0;
  while (x < width + cycleWidth) {
    // Sawtooth flutter waves (F waves)
    path += ` L ${x + cycleWidth * 0.25} ${midY - height * 0.15}`;
    path += ` L ${x + cycleWidth * 0.5} ${midY + height * 0.1}`;
    // Every 4th flutter wave gets a QRS
    if (beatCount % 4 === 0) {
      path += ` L ${x + cycleWidth * 0.55} ${midY + height * 0.05}`;
      path += ` L ${x + cycleWidth * 0.6} ${midY - height * 0.4}`;
      path += ` L ${x + cycleWidth * 0.65} ${midY + height * 0.15}`;
      path += ` L ${x + cycleWidth * 0.75} ${midY}`;
    }
    path += ` L ${x + cycleWidth} ${midY}`;
    x += cycleWidth;
    beatCount++;
  }
  return path;
}

function generateVFib(width: number, height: number): string {
  const midY = height / 2;
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width) {
    // Chaotic, disorganized oscillations — no identifiable QRS
    const amp = (Math.random() * 0.6 + 0.1) * height;
    const freq = 5 + Math.random() * 15;
    const y = midY + (Math.random() - 0.5) * amp;
    path += ` L ${x + freq} ${y}`;
    x += freq;
  }
  return path;
}

function generateVTach(width: number, height: number): string {
  const midY = height / 2;
  const cycleWidth = 35; // Fast, regular, wide QRS
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width + cycleWidth) {
    // Wide bizarre QRS — no P waves
    path += ` L ${x + cycleWidth * 0.05} ${midY + height * 0.1}`;
    path += ` L ${x + cycleWidth * 0.15} ${midY - height * 0.42}`;
    path += ` L ${x + cycleWidth * 0.35} ${midY + height * 0.35}`;
    path += ` L ${x + cycleWidth * 0.55} ${midY - height * 0.1}`;
    path += ` L ${x + cycleWidth * 0.7} ${midY + height * 0.05}`;
    path += ` L ${x + cycleWidth} ${midY}`;
    x += cycleWidth;
  }
  return path;
}

function generateSVT(width: number, height: number): string {
  const midY = height / 2;
  const cycleWidth = 28; // Very fast, narrow QRS
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width + cycleWidth) {
    // No P waves, narrow QRS, rapid rate
    path += ` L ${x + cycleWidth * 0.3} ${midY}`;
    path += ` L ${x + cycleWidth * 0.35} ${midY + height * 0.05}`;
    path += ` L ${x + cycleWidth * 0.4} ${midY - height * 0.38}`;
    path += ` L ${x + cycleWidth * 0.45} ${midY + height * 0.12}`;
    path += ` L ${x + cycleWidth * 0.55} ${midY}`;
    // Small T wave
    path += ` Q ${x + cycleWidth * 0.7} ${midY - height * 0.06} ${x + cycleWidth * 0.8} ${midY}`;
    path += ` L ${x + cycleWidth} ${midY}`;
    x += cycleWidth;
  }
  return path;
}

function generateAsystole(width: number, height: number): string {
  const midY = height / 2;
  // Flatline with very slight drift
  let path = `M 0 ${midY}`;
  for (let x = 0; x < width; x += 20) {
    const drift = (Math.random() - 0.5) * height * 0.03;
    path += ` L ${x + 20} ${midY + drift}`;
  }
  return path;
}

function generateHyperkalaemia(hr: number, width: number, height: number): string {
  const midY = height / 2;
  const cycleWidth = Math.max(50, Math.min(130, 4800 / hr));
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width + cycleWidth) {
    // Absent P, Wide QRS, Peaked (tall) T waves
    path += ` L ${x + cycleWidth * 0.15} ${midY}`;
    // Wide QRS
    path += ` L ${x + cycleWidth * 0.2} ${midY + height * 0.08}`;
    path += ` L ${x + cycleWidth * 0.28} ${midY - height * 0.35}`;
    path += ` L ${x + cycleWidth * 0.38} ${midY + height * 0.2}`;
    path += ` L ${x + cycleWidth * 0.45} ${midY}`;
    // Peaked T wave — the hallmark
    path += ` Q ${x + cycleWidth * 0.55} ${midY - height * 0.4} ${x + cycleWidth * 0.65} ${midY}`;
    path += ` L ${x + cycleWidth} ${midY}`;
    x += cycleWidth;
  }
  return path;
}

// Master ECG path generator
function generateECGPath(hr: number, width: number, height: number, rhythm: ECGRhythm): string {
  switch (rhythm) {
    case "afib": return generateAFib(width, height);
    case "aflutter": return generateAFlutter(width, height);
    case "vfib": return generateVFib(width, height);
    case "vtach": return generateVTach(width, height);
    case "svt": return generateSVT(width, height);
    case "asystole": return generateAsystole(width, height);
    case "hyperkalaemia": return generateHyperkalaemia(hr, width, height);
    case "bradycardia": return generateNormalSinus(Math.min(hr, 50), width, height);
    case "tachycardia": return generateNormalSinus(Math.max(hr, 120), width, height);
    case "normal_sinus":
    default:
      return generateNormalSinus(hr, width, height);
  }
}

// SpO2 pleth waveform
function generatePlethPath(width: number, height: number): string {
  const midY = height * 0.6;
  const cycleWidth = 60;
  let path = `M 0 ${midY}`;
  let x = 0;
  while (x < width + cycleWidth) {
    path += ` Q ${x + cycleWidth * 0.1} ${midY} ${x + cycleWidth * 0.2} ${height * 0.15}`;
    path += ` Q ${x + cycleWidth * 0.3} ${midY * 0.5} ${x + cycleWidth * 0.35} ${midY * 0.55}`;
    path += ` Q ${x + cycleWidth * 0.4} ${midY * 0.45} ${x + cycleWidth * 0.45} ${midY * 0.6}`;
    path += ` Q ${x + cycleWidth * 0.7} ${midY * 0.9} ${x + cycleWidth} ${midY}`;
    x += cycleWidth;
  }
  return path;
}

// Auto-detect rhythm from vitals if none explicitly provided
function detectRhythm(vitals: VitalSigns): ECGRhythm {
  if (vitals.hr === 0) return "asystole";
  if (vitals.hr > 180) return "svt";
  if (vitals.hr > 140) return "tachycardia";
  if (vitals.hr < 40) return "bradycardia";
  return "normal_sinus";
}

function AltairComponent({ onCaseComplete }: AltairProps) {
  const [vitals, setVitals] = useState<VitalSigns | null>(null);
  const [findings, setFindings] = useState<string | null>(null);
  const [rhythm, setRhythm] = useState<ECGRhythm>("normal_sinus");
  const [isVisible, setIsVisible] = useState(false);
  const { client, scenarioContext } = useLiveAPIContext();
  const animationRef = useRef<number>(0);

  const ecgOffsetRef = useRef(0);
  const plethOffsetRef = useRef(0);
  const ecgPathRef = useRef<SVGPathElement>(null);
  const plethPathRef = useRef<SVGPathElement>(null);

  // Determine alert states
  const hrAlert = vitals ? (vitals.hr < 50 || vitals.hr > 120) : false;
  const spo2Alert = vitals ? (vitals.spo2 < 92) : false;
  const bpAlert = vitals ? (vitals.bp_sys > 160 || vitals.bp_sys < 90 || vitals.bp_dia > 100 || vitals.bp_dia < 50) : false;
  const rrAlert = vitals ? (vitals.rr < 10 || vitals.rr > 25) : false;
  const tempAlert = vitals ? (vitals.temp < 35.5 || vitals.temp > 38.5) : false;
  const hasAnyAlert = hrAlert || spo2Alert || bpAlert || rrAlert || tempAlert;

  // Hospital monitor sounds
  const { isMuted, toggleMute } = useMonitorAudio({
    vitals,
    isVisible,
    hasAlert: hasAnyAlert,
  });

  // Clear monitor when scenario changes
  useEffect(() => {
    if (!scenarioContext) {
      setVitals(null);
      setFindings(null);
      setRhythm("normal_sinus");
      setIsVisible(false);
    }
  }, [scenarioContext]);

  // Animate waveforms
  useEffect(() => {
    if (!vitals || !isVisible) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    const speed = Math.max(0.3, vitals.hr / 60);

    const animate = () => {
      ecgOffsetRef.current -= 1.2 * speed;
      plethOffsetRef.current -= 0.8 * speed;

      if (ecgPathRef.current) {
        ecgPathRef.current.setAttribute("transform", `translate(${ecgOffsetRef.current}, 0)`);
      }
      if (plethPathRef.current) {
        plethPathRef.current.setAttribute("transform", `translate(${plethOffsetRef.current}, 0)`);
      }

      const cycleWidth = Math.max(40, Math.min(120, 4800 / (vitals.hr || 1)));
      if (Math.abs(ecgOffsetRef.current) > cycleWidth) {
        ecgOffsetRef.current += cycleWidth;
      }
      if (Math.abs(plethOffsetRef.current) > 60) {
        plethOffsetRef.current += 60;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [vitals, isVisible]);

  // Handle tool calls
  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
      console.log(`got toolcall`, toolCall);

      // 1. Handle Vital Signs & Findings
      const renderAltairCall = toolCall.functionCalls.find(
        (fc) => fc.name === RENDER_ALTAIR_NAME
      );
      if (renderAltairCall) {
        try {
          const args = renderAltairCall.args as any;
          const str = args.json_graph;
          const parsed = JSON.parse(str);

          if (args.findings) {
            setFindings(args.findings);
          }

          // Set rhythm if explicitly provided
          if (args.rhythm) {
            const r = args.rhythm.toLowerCase().replace(/[\s-]/g, "_") as ECGRhythm;
            setRhythm(r);
          }

          if (parsed.hr !== undefined || parsed.HR !== undefined) {
            const normalizedVitals: VitalSigns = {
              hr: parsed.hr || parsed.HR || 80,
              bp_sys: parsed.bp_sys || parsed.BP_SYS || parsed.systolic || 120,
              bp_dia: parsed.bp_dia || parsed.BP_DIA || parsed.diastolic || 80,
              spo2: parsed.spo2 || parsed.SPO2 || parsed.SpO2 || 98,
              rr: parsed.rr || parsed.RR || 16,
              temp: parsed.temp || parsed.TEMP || parsed.temperature || 37.0,
            };
            setVitals(normalizedVitals);
            setIsVisible(true);

            // Auto-detect rhythm if not explicitly set
            if (!args.rhythm) {
              setRhythm(detectRhythm(normalizedVitals));
            }
          }
        } catch (e) {
          console.error("Failed to parse vital signs:", e);
        }
      }

      // 2. Handle Automated Session Summary
      const submitSummaryCall = toolCall.functionCalls.find(
        (fc) => fc.name === "submit_session_summary"
      );
      if (submitSummaryCall) {
        const args = submitSummaryCall.args as any;
        if (onCaseComplete) {
          onCaseComplete({
            ipsScore: args.ipsScore,
            dataGatheringScore: args.dataGatheringScore,
            diagnosisMgtScore: args.diagnosisMgtScore,
            areasForImprovement: args.areasForImprovement,
          });
        }
      }

      if (toolCall.functionCalls.length) {
        setTimeout(
          () =>
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: { output: { success: true } },
                id: fc.id,
              })),
            }),
          200
        );
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, onCaseComplete]);

  // Auto-close vitals monitor after 5 seconds
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isVisible) {
      // Clear any existing timer
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
      // Set new 5-second auto-close timer
      autoCloseTimerRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    }
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, [isVisible, vitals]); // vitals dependency resets timer when new vitals arrive

  const handleDismiss = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
    setIsVisible(false);
  }, []);

  if (!vitals || !isVisible) {
    return null;
  }

  const ecgPath = generateECGPath(vitals.hr, 600, 60, rhythm);
  const plethPath = generatePlethPath(600, 40);

  // Get rhythm display name
  const rhythmLabels: Record<ECGRhythm, string> = {
    normal_sinus: "NSR",
    afib: "AF",
    aflutter: "AFlutter",
    vfib: "VF",
    vtach: "VT",
    svt: "SVT",
    asystole: "ASYSTOLE",
    bradycardia: "Sinus Brady",
    tachycardia: "Sinus Tachy",
    hyperkalaemia: "HyperK",
  };

  return (
    <div className="vital-signs-monitor-overlay">
      <div className="vital-signs-monitor">
        {/* Header */}
        <div className="monitor-header">
          <div className="patient-info">
            <span className="name">PATIENT MONITOR</span>
            <span className="status">{rhythmLabels[rhythm]}</span>
          </div>
          <div className="monitor-controls">
            <button
              className={`mute-button ${isMuted ? 'muted' : ''}`}
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
            </button>
            <button onClick={handleDismiss} title="Close monitor">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Waveforms & Vitals */}
        <div className="monitor-main">
          {/* ECG Waveform */}
          <div className="waveforms">
            <div className="waveform-group">
              <div className="waveform-label" style={{ color: '#00ff00' }}>II</div>
              <svg viewBox="0 0 400 60" preserveAspectRatio="none" style={{ height: '55px' }}>
                <defs>
                  <clipPath id="ecg-clip">
                    <rect x="0" y="0" width="400" height="60" />
                  </clipPath>
                </defs>
                <g clipPath="url(#ecg-clip)">
                  <path
                    ref={ecgPathRef}
                    d={ecgPath}
                    fill="none"
                    stroke="#00ff00"
                    strokeWidth="1.5"
                  />
                </g>
              </svg>
            </div>

            {/* SpO2 Pleth Waveform */}
            <div className="waveform-group">
              <div className="waveform-label" style={{ color: '#00ffff' }}>Pleth</div>
              <svg viewBox="0 0 400 40" preserveAspectRatio="none" style={{ height: '38px' }}>
                <defs>
                  <clipPath id="pleth-clip">
                    <rect x="0" y="0" width="400" height="40" />
                  </clipPath>
                </defs>
                <g clipPath="url(#pleth-clip)">
                  <path
                    ref={plethPathRef}
                    d={plethPath}
                    fill="none"
                    stroke="#00ffff"
                    strokeWidth="1.5"
                  />
                </g>
              </svg>
            </div>
          </div>

          {/* Vitals Grid */}
          <div className="vitals-grid">
            <div className={`vital-card hr-card ${hrAlert ? 'alert' : ''}`}>
              <div className="vital-label">HR</div>
              <div className="vital-value">{vitals.hr}</div>
              <div className="vital-unit">bpm</div>
            </div>

            <div className={`vital-card spo2-card ${spo2Alert ? 'alert' : ''}`}>
              <div className="vital-label">SpO₂</div>
              <div className="vital-value">{vitals.spo2}</div>
              <div className="vital-unit">%</div>
            </div>

            <div className={`vital-card bp-card ${bpAlert ? 'alert' : ''}`}>
              <div className="vital-label">NIBP</div>
              <div className="vital-value">{vitals.bp_sys}/{vitals.bp_dia}</div>
              <div className="vital-unit">mmHg</div>
            </div>

            <div className={`vital-card rr-card ${rrAlert ? 'alert' : ''}`}>
              <div className="vital-label">RR</div>
              <div className="vital-value">{vitals.rr}</div>
              <div className="vital-unit">/min</div>
            </div>

            <div className={`vital-card temp-card ${tempAlert ? 'alert' : ''}`}>
              <div className="vital-label">Temp</div>
              <div className="vital-value">{vitals.temp.toFixed(1)}</div>
              <div className="vital-unit">°C</div>
            </div>
          </div>
        </div>

        {/* Findings Card */}
        {findings && (
          <div className="findings-card">
            <div className="findings-label">EXAMINATION / INVESTIGATION FINDINGS</div>
            <div className="findings-content">{findings}</div>
          </div>
        )}
      </div>
    </div>
  );
}


export const Altair = memo(AltairComponent);
